import React, { useRef } from 'react'
import { FcpCalloutFragment } from '@gql'
import { FlexibleContent } from '../flexibleContent'
import { nonNull } from '../../../utils/nonNull'
import { Grid, GridSize } from '@material-ui/core'
import { parseHtml } from '../../../utils/htmlParser'
import { Button } from '../../button/button'
import styled, { css } from 'styled-components'
import { irem, rem } from '../../../utils'
import { AltBg } from './altBg'
import { Icon } from '../../icon/icon'
import { CreditUnionIcon, IconCodeType } from '../../icon/CreditUnionIcon'
import { faCheckCircle } from '@fortawesome/pro-light-svg-icons'
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons'
import Equalizer from 'react-equalizer'

type CallOutBlock = NonNullable<FcpCalloutFragment['blocks'][number]>

const renderIcon = (block: CallOutBlock) =>
  block.icon ? (
    <CreditUnionIcon variant={block.icon?.variant === 'legacy' ? 'dualtone' : (block.icon?.variant as 'line' | 'monotone' | 'dualtone')} iconCode={block.icon?.icon as IconCodeType} />
  ) : (
    <Icon icon={faCheckCircle} />
  )

export const CallOut: React.FC<FcpCalloutFragment> = ({ blocks, callOutCardType }) => {
  const sameHeightRefs = useRef<(HTMLDivElement | HTMLAnchorElement)[]>([])

  if (!blocks?.length) {
    return null
  }

  // Wide variant — elevated white cards matching the "fcp-features--cards"
  // reference. When a card has a link the whole card is the anchor (so the
  // cursor + hover lift read as clickable) and the CTA is a visual cue, not a
  // nested anchor. CSS grid handles per-row equal heights, so no Equalizer.
  if (callOutCardType) {
    return (
      <FlexibleContent row>
        <WideList>
          {nonNull(blocks).map((block, i) => {
            const url = block.button?.url

            return (
              <WideCard
                key={i}
                $clickable={!!url}
                as={url ? 'a' : 'div'}
                href={url ?? undefined}
                target={url ? (block.button?.target ?? '_self') : undefined}
              >
                <WideIcon>{renderIcon(block)}</WideIcon>
                <WideTitle>{block.heading}</WideTitle>
                {block.subText && (
                  <WideText>{parseHtml(block.subText)}</WideText>
                )}
                {url && (
                  <>
                    <WideArrow icon={faChevronRight} />
                    <WideCta>
                      {block.button?.title ?? 'Learn more'}
                      <WideCtaIcon icon={faChevronRight} />
                    </WideCta>
                  </>
                )}
              </WideCard>
            )
          })}
        </WideList>
      </FlexibleContent>
    )
  }

  const spacing = blocks.length <= 3 ? 6 : 4

  return (
    <FlexibleContent row>
      <Equalizer enabled={() => sameHeightRefs.current.length > 0} nodes={() => sameHeightRefs.current}>
        <Grid container spacing={spacing}>
          {nonNull(blocks).map((block, i) => (
            <StyledItem key={i} xs={12} sm={6} md={(12 / blocks.length) as GridSize}>
              {/** @ts-ignore */}
              <StyledCard $variant={block.button?.url ? 'link' : 'text'} component={block.button?.url ? 'a' : 'div'} href={block.button?.url} ref={(e: HTMLDivElement | HTMLAnchorElement) => sameHeightRefs.current[i] = e}>
                <StyledHeader>
                  <StyledIconWrapper>
                    {renderIcon(block)}
                  </StyledIconWrapper>
                  <StyledBody>
                    <StyledHeading>{block.heading}</StyledHeading>
                    {block.subText && (
                      <StyledSubtext>
                        {parseHtml(block.subText)}
                      </StyledSubtext>
                    )}
                  </StyledBody>
                  {block.button?.url && (
                    <StyledLinkIcon icon={faChevronRight} />
                  )}
                </StyledHeader>
                {block.button?.url && (
                  <StyledButton href={block.button.url} target={block.button.target ?? '_self'} text icon={faChevronRight}>{block.button.title ?? 'Learn more'}</StyledButton>
                )}
              </StyledCard>
            </StyledItem>
          ))}
        </Grid>
      </Equalizer>
    </FlexibleContent>
  )
}

const StyledLinkIcon = styled(Icon)`
    margin-left: auto;
    height: ${irem(15)};
    
    ${props => props.theme.breakpoints.up('md')} {
        display: none;
    }
`

const StyledItem = styled(Grid).attrs({ item: true })`
    & + && {
        margin-top: ${irem(10)};
    }
`

const StyledIconWrapper = styled.div`
    svg, img {
        display: block;
        width: ${irem(45)};
        height: auto;
        
        ${props => props.theme.breakpoints.up('md')} {
            height: ${irem(45)};
        }
    }
`

const StyledCard = styled(Grid)<{ $variant: 'text' | 'link' }>`
    display: block;
    border-radius: ${props => rem(12, props.theme)};
    background: ${props => props.theme.custom.colors.orange};
    text-decoration: none;
    color: inherit;
    transition: background 0.1s ease-in-out;
    
    ${props => props.$variant === 'link' ? css`
        padding: ${irem(12.5)} ${irem(20)};
        min-height: ${irem(70)};
        
        &:hover, &:focus {
            background: ${props => props.theme.custom.colors.orangeHover};
            
            ${StyledButton} {
                color: ${props => props.theme.custom.colors.darkRed};
            }
        }
    ` : css`
        padding: ${irem(25)} ${irem(20)};
        
        ${StyledHeader} {
            align-items: flex-start;
        }
    `}

    ${AltBg} & {
        background: white;
    }

    ${props => props.theme.breakpoints.up('md')} {
        padding: ${props => rem(30, props.theme)};
    }
`

const StyledHeader = styled.div`
    display: flex;
    align-items: center;
    gap: ${irem(16)};
    color: ${props => props.theme.custom.colors.darkRed};
    fill: currentColor;
    
    ${props => props.theme.breakpoints.up('md')} {
        flex-direction: column;
        align-items: flex-start;
        gap: ${irem(20)};
    }
`

const StyledHeading = styled.h3`
    font-size: ${irem(18)};
    font-weight: 600;
    margin: 0;
    
    ${props => props.theme.breakpoints.up('md')} {
        font-size: ${irem(24)};
    }
`

const StyledBody = styled.div`
    line-height: ${irem(22)};
    
    ${props => props.theme.breakpoints.up('md')} {
        line-height: ${irem(28)};
    }
`

const StyledSubtext = styled.div`
    color: ${props => props.theme.palette.text.primary};
    margin-top: ${irem(7)};
    font-size: ${irem(15)};
    
    ${props => props.theme.breakpoints.up('md')} {
        margin-top: ${irem(15)};
    }
`

const StyledButton = styled(Button)`
    text-decoration: none;
    padding: 0;
    display: none;
    transition: color 0.1s ease-in-out;

    ${props => props.theme.breakpoints.up('md')} {
        margin-top: ${irem(50)};
        display: inline-flex;
    }
`

/* ===== Wide variant (callOutCardType) — "fcp-features--cards" reference =====
   Tokens the theme has no equivalent for are pinned as local consts; colours
   that already exist are mapped (no theme.ts edits, no duplicated values). */
const LINE = '#ececec'
const R_MD = '12px'
const SHADOW_MD = '0 18px 40px -18px rgba(28, 31, 42, .26)'
const SHADOW_LG = '0 40px 90px -30px rgba(28, 31, 42, .45)'

const WideList = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: clamp(1.25rem, 3vw, 2rem);
    align-items: stretch;

    @media (max-width: 960px) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 760px) {
        grid-template-columns: 1fr;
    }
`

const WideCard = styled.div<{ $clickable?: boolean; href?: string; target?: string }>`
    position: relative;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 1.1rem;
    padding: clamp(1.6rem, 3vw, 2.2rem);
    background: #fff;
    border: 1px solid ${LINE};
    border-radius: ${R_MD};
    box-shadow: ${SHADOW_MD};
    color: inherit;
    text-decoration: none;
    cursor: ${props => (props.$clickable ? 'pointer' : 'default')};
    transition: transform 0.2s cubic-bezier(0.2, 0.7, 0.2, 1), box-shadow 0.25s ease;

    &:hover {
        transform: translateY(-4px);
        box-shadow: ${SHADOW_LG};
    }

    @media (prefers-reduced-motion: reduce) {
        transition: none;

        &:hover {
            transform: none;
        }
    }
`

const WideIcon = styled.div`
    svg, img {
        display: block;
        width: 60px;
        height: 60px;
    }
`

const WideTitle = styled.h3`
    margin: 0;
    font-size: 1.25rem;
    font-weight: 700;
    line-height: 1.2;
    color: ${props => props.theme.palette.text.primary};
`

const WideText = styled.div`
    font-size: 1rem;
    line-height: 1.6;
    color: ${props => props.theme.palette.text.secondary};
`

/* Mobile: a right-aligned chevron vertically centred against the card's
   height and pinned to the right (same right padding as the card). Hidden
   from md up. */
const WideArrow = styled(Icon)`
    position: absolute;
    top: 50%;
    right: clamp(1.6rem, 3vw, 2.2rem);
    transform: translateY(-50%);
    height: ${irem(20)};
    color: ${props => props.theme.palette.primary.main};

    ${props => props.theme.breakpoints.up('md')} {
        display: none;
    }
`

/* Desktop CTA ("Learn more"): hidden on mobile, shown from md up — the
   inverse of WideArrow, mirroring the Compact card's behaviour. */
const WideCta = styled.span`
    display: none;
    align-items: center;
    gap: 0.4rem;
    margin-top: auto;
    font-weight: 500;
    color: ${props => props.theme.palette.primary.main};

    ${props => props.theme.breakpoints.up('md')} {
        display: inline-flex;
    }
`

const WideCtaIcon = styled(Icon)`
    height: 0.75em;
`